import { BASE_URL, buildImageUrl, getFileUrl } from "../helper/constants";

/** Input cap: 200 MB (matches the backend upload limit) — compressed to 720p. */
export const MAX_VIDEO_SIZE = 200 * 1000 * 1000;
export const MAX_VIDEO_DURATION_SEC = 15 * 60;

export const VIDEO_EXTENSIONS = ["mp4", "mov", "mpg", "mpeg", "webm", "m4v"];

export const VIDEO_ACCEPT_ATTRIBUTE = [
  ".mp4",
  ".mov",
  ".mpg",
  ".mpeg",
  ".webm",
  ".m4v",
  "video/mp4",
  "video/quicktime",
  "video/webm",
].join(",");

export const PHOTO_VIDEO_ACCEPT_ATTRIBUTE =
  ".jpg,.jpeg,.png,.gif,.bmp,.webp,.heic,.HEIC," + VIDEO_ACCEPT_ATTRIBUTE;

export const PHOTO_EXTENSIONS = ["jpg", "jpeg", "png", "gif", "bmp", "webp", "heic", "heif"];

export const isPhotoFile = (file) => {
  if (!file) return false;
  const ext = file.name?.split(".").pop()?.toLowerCase();
  if (PHOTO_EXTENSIONS.includes(ext)) return true;
  const mime = String(file.type || "").toLowerCase();
  return mime.startsWith("image/") && mime !== "image/svg+xml";
};

export const isVideoFile = (file) => {
  if (!file) return false;
  if (file.type && String(file.type).toLowerCase().startsWith("video/")) return true;
  const ext = file.name?.split(".").pop()?.toLowerCase();
  return VIDEO_EXTENSIONS.includes(ext);
};

export const isNativeVideoAttachment = (item) => {
  if (!item) return false;
  const fileType = String(item.fileType || "").toLowerCase();
  const mime = String(item.type || "").toLowerCase();
  if (
    fileType === "image" ||
    fileType === "doc" ||
    item.isPdf ||
    mime.startsWith("image/") ||
    mime === "application/pdf"
  ) {
    return false;
  }
  if (fileType === "video" || item.isVideo) return true;
  if (mime.startsWith("video/")) return true;
  const candidates = [item.fileUrl, item.href, item.name, item.attachedFileName];
  for (const c of candidates) {
    const value = Array.isArray(c) ? c[0] : c;
    if (typeof value !== "string" || value.startsWith("blob:") || value.startsWith("data:")) continue;
    if (/\.(mp4|mov|mpg|mpeg|webm|m4v)(\?|$)/i.test(value)) return true;
  }
  return false;
};

export const ARTICLE_MEDIA_ACCEPT = [
  ".jpg",
  ".jpeg",
  ".png",
  ".pdf",
  "image/jpeg",
  "image/png",
  "application/pdf",
  VIDEO_ACCEPT_ATTRIBUTE,
].join(",");

export const isVideoOversized = (file) => (file?.size || 0) > MAX_VIDEO_SIZE;

export const formatVideoDuration = (seconds) => {
  const total = Math.max(0, Math.round(Number(seconds) || 0));
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
};

export const getVideoMimeType = (fileUrl) => {
  const path = getFileUrl(fileUrl) || "";
  const ext = path.split(".").pop()?.toLowerCase();
  if (ext === "webm") return "video/webm";
  if (ext === "mov") return "video/mp4";
  return "video/mp4";
};

export const getVideoPosterPath = (thumbnailUrl) => {
  const thumb = getFileUrl(thumbnailUrl);
  if (thumb) return thumb;
  // Older videos have no JPEG poster. Do not guess a sibling .jpg — that
  // 404s as JSON and shows as a broken image in <img> tags.
  return "";
};

export const buildVideoPosterUrl = (isLocal, thumbnailUrl, fileUrl) => {
  const path = getVideoPosterPath(thumbnailUrl, fileUrl);
  if (!path) return "";
  if (isLocal) return path;
  if (path.startsWith("blob:") || path.startsWith("data:") || path.startsWith("http")) {
    return path;
  }
  return `${BASE_URL}${path}`;
};

export const buildVideoSrc = (isLocal, fileUrl, attachedFileName) =>
  buildImageUrl(isLocal, fileUrl, attachedFileName);

const readVideoDuration = (file) =>
  new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const video = document.createElement("video");
    video.preload = "metadata";
    video.muted = true;
    video.playsInline = true;
    const cleanup = () => URL.revokeObjectURL(url);
    video.onloadedmetadata = () => {
      const duration = video.duration;
      cleanup();
      if (!Number.isFinite(duration) || duration <= 0) {
        reject(new Error("invalid"));
        return;
      }
      resolve(duration);
    };
    video.onerror = () => {
      cleanup();
      reject(new Error("invalid"));
    };
    video.src = url;
  });

/**
 * Size budget for the compressed result, which also sets the bitrate. Chosen to
 * keep a single-request upload well clear of the 200 MB server cap while still
 * leaving room for a watchable bitrate.
 */
const COMPRESSED_BUDGET_BYTES = 60 * 1000 * 1000;
/** Enough for 720p30; the budget lowers it on long videos. */
const PREFERRED_VIDEO_BITRATE = 2_500_000;
const MIN_VIDEO_BITRATE = 800_000;

/** How often the draw loop may report progress / read the audio level. */
const PROGRESS_INTERVAL_MS = 250;
const AUDIO_SAMPLE_INTERVAL_MS = 100;
const MAX_COMPRESS_WIDTH = 1280;
const MAX_COMPRESS_HEIGHT = 720;
const SD_COMPRESS_WIDTH = 854;
const SD_COMPRESS_HEIGHT = 480;
/**
 * Bitrate below which 720p breaks up. A long video that cannot be given more
 * than this is encoded at a smaller frame size instead, which looks far better
 * than a starved 720p picture.
 */
const MIN_720P_BITRATE = 1_500_000;
const COMPRESS_FPS = 30;
const AUDIO_BITRATE = 128_000;

/** Peak amplitude below which a captured track counts as pure silence. */
const SILENT_PEAK = 0.0005;

/**
 * Whether the source really carries audio. Needed to tell a failed capture
 * apart from a video that is silent to begin with — without it every silent
 * video would look like a capture failure and be uploaded uncompressed.
 * Reads false when the browser exposes no way to tell, which keeps the
 * compressed result rather than falling back to a much larger upload.
 */
const sourceDecodedAudio = (video) => {
  if (typeof video.mozHasAudio === "boolean") return video.mozHasAudio;
  if (typeof video.webkitAudioDecodedByteCount === "number") {
    return video.webkitAudioDecodedByteCount > 0;
  }
  if (video.audioTracks && typeof video.audioTracks.length === "number") {
    return video.audioTracks.length > 0;
  }
  return false;
};

const waitForEvent = (el, event) =>
  new Promise((resolve, reject) => {
    const onOk = () => {
      el.removeEventListener("error", onErr);
      resolve();
    };
    const onErr = () => {
      el.removeEventListener(event, onOk);
      reject(new Error(event));
    };
    el.addEventListener(event, onOk, { once: true });
    el.addEventListener("error", onErr, { once: true });
  });

const pickRecorderMime = () => {
  const types = [
    "video/webm;codecs=vp9,opus",
    "video/webm;codecs=vp8,opus",
    "video/webm",
    "video/mp4;codecs=avc1,mp4a.40.2",
    "video/mp4",
  ];
  if (typeof MediaRecorder === "undefined" || !MediaRecorder.isTypeSupported) {
    return "";
  }
  return types.find((type) => MediaRecorder.isTypeSupported(type)) || "";
};

const fitEvenSize = (width, height, maxW, maxH) => {
  if (!width || !height) return { width: 1280, height: 720 };
  const scale = Math.min(1, maxW / width, maxH / height);
  return {
    width: Math.max(2, Math.round((width * scale) / 2) * 2),
    height: Math.max(2, Math.round((height * scale) / 2) * 2),
  };
};

/**
 * Spend as much bitrate as the size budget allows, up to a quality ceiling.
 *
 * Dividing a single 12 MB target by the duration starved everything longer than
 * about a minute: the result fell to the floor and stayed there, which is what
 * made longer videos look blocky. Deriving the rate from a realistic budget
 * roughly doubles it for the common one-to-three-minute clip.
 */
const targetVideoBitrate = (durationSec) => {
  const safeDuration = Math.max(1, Number(durationSec) || 1);
  const budgetBps = (COMPRESSED_BUDGET_BYTES * 8) / safeDuration - AUDIO_BITRATE;
  return Math.round(
    Math.max(MIN_VIDEO_BITRATE, Math.min(PREFERRED_VIDEO_BITRATE, budgetBps))
  );
};

/**
 * Re-encode to 720p (480p once the size budget can no longer feed 720p) at up to
 * 2.5 Mbps — roughly the quality tier WhatsApp and Telegram use. The server
 * re-encodes with CRF afterwards, so keep this source as clean as the budget
 * allows.
 */
export const compressChatVideo = async (file, { onProgress } = {}) => {
  if (!file) return file;
  if (typeof document === "undefined" || typeof MediaRecorder === "undefined") {
    return file;
  }
  const mimeType = pickRecorderMime();
  if (!mimeType) return file;

  const srcUrl = file.url || URL.createObjectURL(file);
  const ownsSrcUrl = !file.url;
  let audioCtx;
  const tracksToStop = [];

  try {
    const video = document.createElement("video");
    video.muted = true;
    video.playsInline = true;
    video.preload = "auto";
    video.src = srcUrl;
    await waitForEvent(video, "loadedmetadata");
    if (video.readyState < 2) {
      await waitForEvent(video, "loadeddata");
    }

    const duration = Number.isFinite(video.duration) ? video.duration : file.duration || 0;
    const videoBitrate = targetVideoBitrate(duration);
    const fitsHd = videoBitrate >= MIN_720P_BITRATE;
    const { width, height } = fitEvenSize(
      video.videoWidth,
      video.videoHeight,
      fitsHd ? MAX_COMPRESS_WIDTH : SD_COMPRESS_WIDTH,
      fitsHd ? MAX_COMPRESS_HEIGHT : SD_COMPRESS_HEIGHT
    );

    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext("2d", { alpha: false, desynchronized: true });
    if (!ctx) return file;
    ctx.drawImage(video, 0, 0, width, height);

    // Capture frame by frame instead of on a fixed clock. captureStream(fps)
    // samples the canvas on its own timer, unrelated to when the draw loop
    // actually paints, so frames were duplicated and dropped unevenly and
    // playback juddered. With 0 the track only emits what requestFrame asks for.
    let canvasStream = canvas.captureStream(0);
    let canvasTrack = canvasStream.getVideoTracks()[0];
    if (!canvasTrack || typeof canvasTrack.requestFrame !== "function") {
      canvasStream = canvas.captureStream(COMPRESS_FPS);
      canvasTrack = null;
    }
    tracksToStop.push(...canvasStream.getVideoTracks());

    let analyser;
    let audioAttached = false;
    try {
      // Chrome zeroes a muted element's audio before it reaches the Web Audio
      // graph (crbug.com/1136404), so leaving the element muted here records
      // silence. createMediaElementSource takes ownership of the element's
      // audio and detaches it from the element's own output, so unmuting does
      // not make anything audible; the gain-0 branch keeps the graph pulling.
      video.muted = false;
      video.volume = 1;
      audioCtx = new (window.AudioContext || window.webkitAudioContext)();
      if (audioCtx.state === "suspended") await audioCtx.resume();
      const source = audioCtx.createMediaElementSource(video);

      analyser = audioCtx.createAnalyser();
      analyser.fftSize = 2048;
      const silencer = audioCtx.createGain();
      silencer.gain.value = 0;

      // A node only receives samples when its path reaches a destination, so
      // the analyser sits inline ahead of the gain-0 branch rather than dangling
      // off the source.
      source.connect(analyser);
      analyser.connect(silencer);
      silencer.connect(audioCtx.destination);

      const dest = audioCtx.createMediaStreamDestination();
      source.connect(dest);

      // A track carrying only silence is reported as muted, and MediaRecorder
      // then emits empty chunks. An inaudible tone keeps it alive.
      const keepAlive = audioCtx.createOscillator();
      const keepAliveGain = audioCtx.createGain();
      keepAliveGain.gain.value = 0;
      keepAlive.connect(keepAliveGain);
      keepAliveGain.connect(dest);
      keepAlive.start(0);

      dest.stream.getAudioTracks().forEach((track) => {
        canvasStream.addTrack(track);
        tracksToStop.push(track);
      });
      audioAttached = canvasStream.getAudioTracks().length > 0;
    } catch (_) {
      /* silent / unsupported audio — video-only is fine */
    }

    const chunks = [];
    const recorder = new MediaRecorder(canvasStream, {
      mimeType,
      videoBitsPerSecond: videoBitrate,
      audioBitsPerSecond: AUDIO_BITRATE,
    });
    recorder.ondataavailable = (event) => {
      if (event.data && event.data.size) chunks.push(event.data);
    };

    const stopped = new Promise((resolve, reject) => {
      recorder.onstop = resolve;
      recorder.onerror = () => reject(new Error("record"));
    });

    const audioSamples = analyser ? new Float32Array(analyser.fftSize) : null;
    let audioPeak = 0;
    const sampleAudioLevel = () => {
      if (!analyser || !audioSamples) return;
      analyser.getFloatTimeDomainData(audioSamples);
      for (let i = 0; i < audioSamples.length; i += 1) {
        const level = Math.abs(audioSamples[i]);
        if (level > audioPeak) audioPeak = level;
      }
    };

    // Everything below is throttled because the draw loop runs once per source
    // frame. Reporting progress from every frame re-rendered the conversation
    // dozens of times a second, and the resulting main-thread stalls made the
    // canvas fall behind, which showed up as dropped frames in the recording.
    let lastProgressAt = 0;
    let lastAudioSampleAt = 0;

    const draw = () => {
      if (video.readyState >= 2) {
        ctx.drawImage(video, 0, 0, width, height);
        if (canvasTrack) canvasTrack.requestFrame();
      }
      const now = performance.now();
      if (now - lastAudioSampleAt >= AUDIO_SAMPLE_INTERVAL_MS) {
        lastAudioSampleAt = now;
        sampleAudioLevel();
      }
      if (onProgress && duration > 0 && now - lastProgressAt >= PROGRESS_INTERVAL_MS) {
        lastProgressAt = now;
        onProgress(Math.min(99, (video.currentTime / duration) * 100));
      }
      if (!video.paused && !video.ended) {
        if (video.requestVideoFrameCallback) video.requestVideoFrameCallback(draw);
        else requestAnimationFrame(draw);
      }
    };

    recorder.start(200);
    video.currentTime = 0;
    await video.play();
    if (video.requestVideoFrameCallback) video.requestVideoFrameCallback(draw);
    else requestAnimationFrame(draw);

    await new Promise((resolve) => {
      const finish = () => resolve();
      video.onended = finish;
      window.setTimeout(finish, Math.ceil((duration + 0.8) * 1000));
    });

    if (recorder.state === "recording") recorder.stop();
    await stopped;
    if (onProgress) onProgress(100);

    // Never trade sound for a smaller file. If the source decoded audio but
    // nothing reached the recorder, the capture failed and the original is the
    // only copy that still has sound.
    if (audioAttached && audioPeak < SILENT_PEAK && sourceDecodedAudio(video)) {
      return file;
    }

    const blobType = mimeType.split(";")[0];
    const blob = new Blob(chunks, { type: blobType });
    if (!blob.size || blob.size >= file.size) return file;

    const ext = blobType.includes("mp4") ? "mp4" : "webm";
    const baseName = String(file.name || "video").replace(/\.[^.]+$/, "");
    const compressed = new File([blob], `${baseName}.${ext}`, { type: blobType });
    compressed.duration = duration;
    compressed.url = URL.createObjectURL(compressed);
    // No second pass: the bitrate is derived from the size budget, so re-running
    // it produced an identical file and doubled the wait.
    return compressed;
  } catch (err) {
    return file;
  } finally {
    tracksToStop.forEach((track) => {
      try { track.stop(); } catch (_) { /* ignore */ }
    });
    if (audioCtx) {
      try { audioCtx.close(); } catch (_) { /* ignore */ }
    }
    if (ownsSrcUrl) URL.revokeObjectURL(srcUrl);
  }
};

export const patchTempVideoMessage = (history, chatId, requestId, patch) =>
  (history || []).map((entry) => {
    if (entry.id !== chatId) return entry;
    return {
      ...entry,
      data: (entry.data || []).map((section) => ({
        ...section,
        items: (section.items || []).map((item) =>
          item.messageRequestId === requestId ? { ...item, ...patch } : item
        ),
      })),
    };
  });

/**
 * Validate size + duration and attach a blob URL for preview.
 * @returns {Promise<{ok:true, file: File}|{ok:false, errorKey:string}>}
 */
export const prepareChatVideo = async (file) => {
  if (!file || !isVideoFile(file)) {
    return { ok: false, errorKey: "video_invalid" };
  }
  if (isVideoOversized(file)) {
    return { ok: false, errorKey: "video_too_large" };
  }
  try {
    const duration = await readVideoDuration(file);
    if (duration > MAX_VIDEO_DURATION_SEC) {
      return { ok: false, errorKey: "video_too_long" };
    }
    if (!file.url) file.url = URL.createObjectURL(file);
    file.duration = duration;
    return { ok: true, file };
  } catch (err) {
    return { ok: false, errorKey: "video_invalid" };
  }
};
